import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Footprints, Trash2 } from "lucide-react";

const L = ({ children }) => <Label className="label-caps text-muted-foreground">{children}</Label>;
const DURATIONS = ["0.5", "1", "1.5", "2"];
const sel = "w-full border border-border/70 rounded-sm px-3 py-2 bg-background text-sm outline-none focus:border-brand";

// Book (lead) or edit (job) a free walk-through visit
export default function WalkthroughDialog({ lead, job, team = [], onClose, onSaved }) {
  const navigate = useNavigate();
  const open = !!(lead || job);
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const d = job?.scheduled_date ? parseISO(job.scheduled_date) : null;
    setF({ date: d ? format(d, "yyyy-MM-dd") : "", time: d ? format(d, "HH:mm") : "09:00",
      duration_hours: String(job?.duration_hours || "0.5"), assigned_team_ids: job?.assigned_team_ids || [],
      notes: job?.notes || "", status: job?.status || "scheduled", notify: true });
  }, [open, job]);

  const name = job?.customer?.name || lead?.name || "Lead";
  const toggle = (id) => setF((x) => ({ ...x, assigned_team_ids: x.assigned_team_ids.includes(id)
    ? x.assigned_team_ids.filter((t) => t !== id) : [...x.assigned_team_ids, id] }));

  const save = async () => {
    if (!f.date || !f.time) return toast.error("Pick a date and time");
    setSaving(true);
    const payload = { scheduled_date: new Date(`${f.date}T${f.time}`).toISOString(), duration_hours: Number(f.duration_hours),
      assigned_team_ids: f.assigned_team_ids, notes: f.notes };
    try {
      if (job) {
        await api.put(`/jobs/${job.id}`, { ...payload, status: f.status });
        toast.success("Walk-through updated");
      } else {
        const { data } = await api.post(`/leads/${lead.id}/walkthrough`, { ...payload, notify: f.notify });
        const c = data.confirmation;
        toast.success(!c ? "Walk-through booked" : c.entries.length === 0 ? "Walk-through booked · no confirmation sent (no mobile/email)"
          : c.entries.some((e) => (e.detail || "").includes("Live sending is off")) ? "Walk-through booked · confirmation logged in SMS test mode"
          : c.sent ? "Walk-through booked · confirmation sent" : "Walk-through booked · confirmation failed (see SMS history)");
      }
      onSaved?.(); onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this walk-through visit?")) return;
    await api.delete(`/jobs/${job.id}`);
    toast.success("Walk-through deleted");
    onSaved?.(); onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="walkthrough-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <Footprints className="w-5 h-5 text-[#0f766e]" /> {job ? "Walk-through visit" : "Book a free walk-through"}
          </DialogTitle>
          <DialogDescription>{name} · no charge, kept out of revenue and payroll.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2"><L>Date</L><Input type="date" value={f.date || ""} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="wt-date" /></div>
            <div className="space-y-2"><L>Time</L><Input type="time" step="900" value={f.time || ""} onChange={(e) => setF({ ...f, time: e.target.value })} data-testid="wt-time" /></div>
            <div className="space-y-2"><L>Length</L>
              <select value={f.duration_hours} onChange={(e) => setF({ ...f, duration_hours: e.target.value })} className={sel} data-testid="wt-duration">
                {DURATIONS.map((d) => <option key={d} value={d}>{d === "0.5" ? "30 min" : `${d} hr`}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-2">
            <L>Team member</L>
            <div className="border border-border/60 rounded-sm divide-y divide-border/60 max-h-36 overflow-y-auto">
              {team.length === 0 && <p className="text-sm text-muted-foreground p-3">No team members yet.</p>}
              {team.map((m) => (
                <label key={m.id} className="flex items-center gap-3 p-2.5 cursor-pointer hover:bg-accent/40" data-testid={`wt-team-${m.id}`}>
                  <input type="checkbox" checked={(f.assigned_team_ids || []).includes(m.id)} onChange={() => toggle(m.id)} className="accent-primary w-4 h-4" />
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: m.color || "#8a817c" }} />
                  <span className="text-sm">{m.name}</span>
                </label>
              ))}
            </div>
          </div>
          {job && (
            <div className="space-y-2"><L>Status</L>
              <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={sel} data-testid="wt-status">
                <option value="scheduled">Scheduled</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option>
              </select>
            </div>
          )}
          <div className="space-y-2"><L>Notes</L><Textarea value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Gate code, areas to look at…" data-testid="wt-notes" /></div>
          {!job && (
            <div className="flex items-center justify-between border border-border/60 rounded-sm px-4 py-3">
              <div>
                <div className="text-sm font-medium">Send confirmation now</div>
                <p className="text-xs text-muted-foreground">A reminder is also sent before the visit, like normal bookings.</p>
              </div>
              <Switch checked={!!f.notify} onCheckedChange={(v) => setF({ ...f, notify: v })} data-testid="wt-notify" />
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          {job && <Button variant="outline" onClick={remove} data-testid="wt-delete" className="rounded-sm gap-2 text-destructive"><Trash2 className="w-4 h-4" /> Delete</Button>}
          {job?.lead_id && <Button variant="ghost" onClick={() => { onClose(); navigate("/leads"); }} data-testid="wt-open-lead" className="rounded-sm">Open lead</Button>}
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose} className="rounded-sm" data-testid="wt-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-sm" data-testid="wt-save">{saving ? "Saving…" : job ? "Save" : "Book walk-through"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
